import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Button,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  DateRange as DateRangeIcon,
  Business as BusinessIcon,
  Flag as FlagIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

import {
  InvoiceFilters,
  DateRangeFilter,
  BusinessUnitFilter,
  StatusFilter,
  ProcessingStatusFilter,
  AmountFilter,
  SearchFilter,
  FilterTemplate
} from '../../types/export';

interface EnhancedFilterBuilderProps {
  filters: InvoiceFilters;
  onFiltersChange: (filters: InvoiceFilters) => void;
  businessUnits: Array<{ id: number; name: string; ico?: string; dic?: string }>;
  filterTemplates: {
    dateRanges: FilterTemplate[];
    businessUnits: FilterTemplate[];
    statuses: FilterTemplate[];
    amounts: FilterTemplate[];
    combined: FilterTemplate[];
  };
}

const EnhancedFilterBuilder: React.FC<EnhancedFilterBuilderProps> = ({
  filters,
  onFiltersChange,
  businessUnits,
  filterTemplates
}) => {
  const [localFilters, setLocalFilters] = useState<InvoiceFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const updateFilters = (newFilters: Partial<InvoiceFilters>) => {
    const updatedFilters = { ...localFilters, ...newFilters };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const applyTemplate = (template: FilterTemplate) => {
    const updatedFilters = { ...localFilters, ...template.filters };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const clearFilters = () => {
    const clearedFilters: InvoiceFilters = {};
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const renderDateRangeSection = () => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <DateRangeIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1">Období</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Quick templates */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Rychlé výběry
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {filterTemplates.dateRanges.map((template) => (
                <Chip
                  key={template.id}
                  label={template.name}
                  onClick={() => applyTemplate(template)}
                  variant="outlined"
                  clickable
                  size="small"
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Vlastní období
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Typ období</InputLabel>
              <Select
                value={localFilters.dateRange?.type || 'custom'}
                onChange={(e) => updateFilters({
                  dateRange: {
                    ...localFilters.dateRange,
                    type: e.target.value as DateRangeFilter['type']
                  }
                })}
                label="Typ období"
              >
                <MenuItem value="custom">Vlastní</MenuItem>
                <MenuItem value="currentMonth">Aktuální měsíc</MenuItem>
                <MenuItem value="lastMonth">Minulý měsíc</MenuItem>
                <MenuItem value="currentQuarter">Aktuální čtvrtletí</MenuItem>
                <MenuItem value="lastQuarter">Minulé čtvrtletí</MenuItem>
                <MenuItem value="currentYear">Aktuální rok</MenuItem>
                <MenuItem value="lastYear">Minulý rok</MenuItem>
                <MenuItem value="last30Days">Posledních 30 dní</MenuItem>
                <MenuItem value="last90Days">Posledních 90 dní</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {localFilters.dateRange?.type === 'custom' && (
            <>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
                  <DatePicker
                    label="Od data"
                    value={localFilters.dateRange?.customDates?.from ? new Date(localFilters.dateRange.customDates.from) : null}
                                         onChange={(date) => updateFilters({
                       dateRange: {
                         type: localFilters.dateRange?.type || 'custom',
                         customDates: {
                           ...localFilters.dateRange?.customDates,
                           from: date ? format(date, 'yyyy-MM-dd') : undefined
                         }
                       }
                     })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
                  <DatePicker
                    label="Do data"
                    value={localFilters.dateRange?.customDates?.to ? new Date(localFilters.dateRange.customDates.to) : null}
                                         onChange={(date) => updateFilters({
                       dateRange: {
                         type: localFilters.dateRange?.type || 'custom',
                         customDates: {
                           ...localFilters.dateRange?.customDates,
                           to: date ? format(date, 'yyyy-MM-dd') : undefined
                         }
                       }
                     })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  const renderBusinessUnitSection = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <BusinessIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1">Účetní jednotky</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Quick templates */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Rychlé výběry
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {filterTemplates.businessUnits.map((template) => (
                <Chip
                  key={template.id}
                  label={template.name}
                  onClick={() => applyTemplate(template)}
                  variant="outlined"
                  clickable
                  size="small"
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Vlastní výběr
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Typ výběru</InputLabel>
              <Select
                value={localFilters.businessUnit?.type || 'all'}
                onChange={(e) => updateFilters({
                  businessUnit: {
                    ...localFilters.businessUnit,
                    type: e.target.value as BusinessUnitFilter['type']
                  }
                })}
                label="Typ výběru"
              >
                <MenuItem value="all">Všechny</MenuItem>
                <MenuItem value="specific">Konkrétní</MenuItem>
                <MenuItem value="unassigned">Nepřiřazené</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {localFilters.businessUnit?.type === 'specific' && (
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Vyberte účetní jednotky:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {businessUnits.map((bu) => (
                  <Chip
                    key={bu.id}
                    label={`${bu.name}${bu.ico ? ` (${bu.ico})` : ''}`}
                    onClick={() => {
                      const currentIds = localFilters.businessUnit?.ids || [];
                      const newIds = currentIds.includes(bu.id)
                        ? currentIds.filter(id => id !== bu.id)
                        : [...currentIds, bu.id];
                      
                                             updateFilters({
                         businessUnit: {
                           type: localFilters.businessUnit?.type || 'all',
                           ids: newIds
                         }
                       });
                    }}
                    variant={localFilters.businessUnit?.ids?.includes(bu.id) ? "filled" : "outlined"}
                    clickable
                    size="small"
                  />
                ))}
              </Stack>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  const renderStatusSection = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <FlagIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1">Stavy</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Quick templates */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Rychlé výběry
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {filterTemplates.statuses.map((template) => (
                <Chip
                  key={template.id}
                  label={template.name}
                  onClick={() => applyTemplate(template)}
                  variant="outlined"
                  clickable
                  size="small"
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Vlastní výběr
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status faktury</InputLabel>
              <Select
                value={localFilters.status?.type || 'all'}
                onChange={(e) => updateFilters({
                  status: {
                    ...localFilters.status,
                    type: e.target.value as StatusFilter['type']
                  }
                })}
                label="Status faktury"
              >
                <MenuItem value="all">Všechny</MenuItem>
                <MenuItem value="specific">Konkrétní</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {localFilters.status?.type === 'specific' && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {['pending', 'approved', 'rejected', 'cancelled'].map((status) => (
                  <Chip
                    key={status}
                    label={status === 'pending' ? 'Čekající' : status === 'approved' ? 'Schválené' : status === 'rejected' ? 'Zamítnuté' : 'Zrušené'}
                    onClick={() => {
                      const currentValues = localFilters.status?.values || [];
                      const newValues = currentValues.includes(status)
                        ? currentValues.filter(v => v !== status)
                        : [...currentValues, status];
                      
                                             updateFilters({
                         status: {
                           type: localFilters.status?.type || 'all',
                           values: newValues
                         }
                       });
                    }}
                    variant={localFilters.status?.values?.includes(status) ? "filled" : "outlined"}
                    clickable
                    size="small"
                  />
                ))}
              </Stack>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  const renderAmountSection = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <MoneyIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1">Částky</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Quick templates */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Rychlé výběry
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {filterTemplates.amounts.map((template) => (
                <Chip
                  key={template.id}
                  label={template.name}
                  onClick={() => applyTemplate(template)}
                  variant="outlined"
                  clickable
                  size="small"
                />
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Vlastní rozsah
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Typ filtru</InputLabel>
              <Select
                value={localFilters.amount?.type || 'all'}
                onChange={(e) => updateFilters({
                  amount: {
                    ...localFilters.amount,
                    type: e.target.value as AmountFilter['type']
                  }
                })}
                label="Typ filtru"
              >
                <MenuItem value="all">Všechny částky</MenuItem>
                <MenuItem value="range">Rozsah</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {localFilters.amount?.type === 'range' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Od částky (Kč)"
                  type="number"
                  value={localFilters.amount?.range?.from || ''}
                                     onChange={(e) => updateFilters({
                     amount: {
                       type: localFilters.amount?.type || 'all',
                       range: {
                         ...localFilters.amount?.range,
                         from: e.target.value ? parseFloat(e.target.value) : undefined
                       }
                     }
                   })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Do částky (Kč)"
                  type="number"
                  value={localFilters.amount?.range?.to || ''}
                                     onChange={(e) => updateFilters({
                     amount: {
                       type: localFilters.amount?.type || 'all',
                       range: {
                         ...localFilters.amount?.range,
                         to: e.target.value ? parseFloat(e.target.value) : undefined
                       }
                     }
                   })}
                />
              </Grid>
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  const renderSearchSection = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SearchIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1">Vyhledávání</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Typ vyhledávání</InputLabel>
              <Select
                value={localFilters.search?.type || 'none'}
                onChange={(e) => updateFilters({
                  search: {
                    ...localFilters.search,
                    type: e.target.value as SearchFilter['type']
                  }
                })}
                label="Typ vyhledávání"
              >
                <MenuItem value="none">Bez vyhledávání</MenuItem>
                <MenuItem value="text">Textové vyhledávání</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {localFilters.search?.type === 'text' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vyhledávaný text"
                value={localFilters.search?.text || ''}
                                 onChange={(e) => updateFilters({
                   search: {
                     type: localFilters.search?.type || 'none',
                     text: e.target.value
                   }
                 })}
                placeholder="Zadejte text pro vyhledávání..."
              />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  const renderCombinedTemplates = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">Kombinované šablony</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {filterTemplates.combined.map((template) => (
            <Grid item xs={12} sm={6} key={template.id}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => applyTemplate(template)}
                  >
                    Použít
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filtry exportu</Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={clearFilters}
        >
          Vymazat filtry
        </Button>
      </Box>

      {renderDateRangeSection()}
      {renderBusinessUnitSection()}
      {renderStatusSection()}
      {renderAmountSection()}
      {renderSearchSection()}
      {renderCombinedTemplates()}

      {/* Filter summary */}
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          Souhrn filtrů
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getFilterSummary(localFilters)}
        </Typography>
      </Box>
    </Box>
  );
};

// Helper function to get filter summary
const getFilterSummary = (filters: InvoiceFilters): string => {
  const parts = [];

  // Date range summary
  if (filters.dateRange) {
    if (filters.dateRange.type === 'custom' && filters.dateRange.customDates) {
      if (filters.dateRange.customDates.from && filters.dateRange.customDates.to) {
        parts.push(`Období: ${filters.dateRange.customDates.from} - ${filters.dateRange.customDates.to}`);
      }
    } else {
      const dateLabels: Record<string, string> = {
        currentMonth: 'Aktuální měsíc',
        lastMonth: 'Minulý měsíc',
        currentQuarter: 'Aktuální čtvrtletí',
        lastQuarter: 'Minulé čtvrtletí',
        currentYear: 'Aktuální rok',
        lastYear: 'Minulý rok',
        last30Days: 'Posledních 30 dní',
        last90Days: 'Posledních 90 dní'
      };
      parts.push(`Období: ${dateLabels[filters.dateRange.type] || filters.dateRange.type}`);
    }
  }

  // Business unit summary
  if (filters.businessUnit) {
    if (filters.businessUnit.type === 'unassigned') {
      parts.push('Účetní jednotka: Nepřiřazené');
    } else if (filters.businessUnit.type === 'specific' && filters.businessUnit.ids) {
      parts.push(`Účetní jednotka: ${filters.businessUnit.ids.length} vybraných`);
    }
  }

  // Status summary
  if (filters.status && filters.status.type === 'specific' && filters.status.values) {
    parts.push(`Status: ${filters.status.values.join(', ')}`);
  }

  // Amount summary
  if (filters.amount && filters.amount.type === 'range' && filters.amount.range) {
    const range = filters.amount.range;
    if (range.from !== undefined && range.to !== undefined) {
      parts.push(`Částka: ${range.from} - ${range.to} Kč`);
    } else if (range.from !== undefined) {
      parts.push(`Částka: od ${range.from} Kč`);
    } else if (range.to !== undefined) {
      parts.push(`Částka: do ${range.to} Kč`);
    }
  }

  // Search summary
  if (filters.search && filters.search.type === 'text' && filters.search.text) {
    parts.push(`Vyhledávání: "${filters.search.text}"`);
  }

  return parts.length > 0 ? parts.join(' | ') : 'Žádné filtry';
};

export default EnhancedFilterBuilder;
